import { useCallback, useMemo } from 'react';
import { useFetcher } from '@remix-run/react';

import {
    BUILDHUB_PROJECTS_API,
    type loader as GetBuildHubsListRemixLoader,
} from '~/routes/remix-api.build-hub.projects';

import type {
    BuildHubViewFragment,
    BuildHubTypeEnum,
    BuildHubRoleEnum,
} from 'generated/graphql';
import type { BuildHubCollaborator } from '@modules/build-hub/types';
import type { DetailedPlace } from '@modules-rocco/location/type/detailed-place';
import { useFeature } from '~/modules/root/hook/use-feature';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';

/**
 * Hook for fetching the list of BuildHubs (projects) the user owns or has access to.
 * Maps BuildHubViewFragment to BuildHubForGrouping for use in rocco components.
 *
 * @returns A tuple containing:
 * - Array of BuildHub data (with ID and Title) or undefined if not yet loaded
 * - Boolean indicating if fetch is in progress
 * - Function to trigger BuildHubs fetch
 *
 * @example
 * const [buildHubs, isLoading, fetchBuildHubs] = useGetBuildHubsList();
 *
 * useEffect(() => {
 *   if (!buildHubs) {
 *     fetchBuildHubs();
 *   }
 * }, [buildHubs, fetchBuildHubs]);
 */
export interface BuildHubOption {
    ID: string;
    Title: string;
    ProjectType?: BuildHubTypeEnum | null;
    ProjectScope?: string | null;
    BuildStage?: string | null;
    Location?: string | null;
    LocationData?: DetailedPlace | null;
    Budget?: {
        Min?: number;
        Max?: number;
    } | null;
    Thumbnail?: string | null;
    StartDate?: string | null;
    EndDate?: string | null;
    Collaborators: BuildHubCollaborator[];
}

/**
 * Hook for fetching the list of BuildHubs (projects) the user owns or has access to.
 * Maps BuildHubViewFragment to BuildHubOption for use in rocco components.
 *
 * @returns A tuple containing:
 * - Array of BuildHub data or undefined if not yet loaded
 * - Boolean indicating if fetch is in progress
 * - Function to trigger BuildHubs fetch
 */
export const useGetBuildHubsList = (): [
    BuildHubOption[] | undefined,
    boolean,
    () => void,
] => {
    const { isLoggedIn } = useUserLoggedIn();
    const isBuildHubEnabled = useFeature('buildhub');
    const fetcher = useFetcher<typeof GetBuildHubsListRemixLoader>();

    const handleFetchBuildHubs = useCallback(() => {
        if (!isBuildHubEnabled || !isLoggedIn) return;
        // Fetch the full list by passing the maximum allowed limit (100)
        fetcher.load(`${BUILDHUB_PROJECTS_API}?limit=100`);
    }, [fetcher, isBuildHubEnabled, isLoggedIn]);

    // Map BuildHubViewFragment to BuildHubOption
    const mappedData = useMemo(
        () =>
            fetcher.data?.nodes.map((buildHub: BuildHubViewFragment) => {
                // Map LocationData from GraphQL (PascalCase) to DetailedPlace (camelCase)
                const locationData: DetailedPlace | null = buildHub.LocationData
                    ? {
                          ...(buildHub.LocationData.Latitude != null && {
                              latitude: buildHub.LocationData.Latitude,
                          }),
                          ...(buildHub.LocationData.Longitude != null && {
                              longitude: buildHub.LocationData.Longitude,
                          }),
                          fullAddress:
                              buildHub.LocationData.FullAddress ?? undefined,
                          address1: buildHub.LocationData.Address1 ?? undefined,
                          address2: buildHub.LocationData.Address2 ?? undefined,
                          suburb: buildHub.LocationData.Suburb ?? undefined,
                          city: buildHub.LocationData.City ?? undefined,
                          region: buildHub.LocationData.Region ?? undefined,
                          state: buildHub.LocationData.State ?? undefined,
                          country: buildHub.LocationData.Country ?? undefined,
                          postCode: buildHub.LocationData.PostCode ?? undefined,
                          placeName:
                              buildHub.LocationData.PlaceName ?? undefined,
                          placeType:
                              buildHub.LocationData.PlaceType ?? undefined,
                          addressID:
                              buildHub.LocationData.AddressID ?? undefined,
                          depth: buildHub.LocationData.Depth ?? undefined,
                          parentID: buildHub.LocationData.ParentID ?? undefined,
                          serviceableAreaID:
                              buildHub.LocationData.ServiceableAreaID ??
                              undefined,
                          district: buildHub.LocationData.District ?? undefined,
                          division: buildHub.LocationData.Division ?? undefined,
                          slug: buildHub.LocationData.Slug ?? undefined,
                      }
                    : null;

                return {
                    ID: buildHub.ID,
                    Title: buildHub.Title,
                    ProjectType: buildHub.ProjectType,
                    ProjectScope: buildHub.ProjectScope,
                    BuildStage: buildHub.BuildStage ?? null,
                    Location: buildHub.Location,
                    LocationData: locationData,
                    Budget: buildHub.Budget,
                    Thumbnail:
                        buildHub.ThumbnailFile?.Detail?.Desktop ||
                        buildHub.ThumbnailFile?.Thumbnail?.Desktop,
                    StartDate: buildHub.StartDate ?? null,
                    EndDate: buildHub.EndDate ?? null,
                    Collaborators: (buildHub.Collaborators || []).map((c) => ({
                        id: c.ID,
                        name: c.Member?.Name || '',
                        email: c.Email || '',
                        image: c.Member?.ProfileImageLink || null,
                        role: c.Role as BuildHubRoleEnum,
                        onBoarded: !!c.Member,
                        sortOrder: c.SortOrder || 0,
                        teamMember: c.TeamMember
                            ? {
                                  id: String(c.TeamMember.ID),
                                  name: c.TeamMember.Name,
                                  jobTitle: c.TeamMember.JobTitle || undefined,
                                  memberImage: c.TeamMember.MemberImage
                                      ? {
                                            id: String(
                                                c.TeamMember.MemberImage.ID
                                            ),
                                            teamMemberDisplay:
                                                c.TeamMember.MemberImage
                                                    .TeamMemberDisplay ||
                                                undefined,
                                        }
                                      : undefined,
                              }
                            : undefined,
                    })),
                };
            }),
        [fetcher.data]
    );

    return [mappedData, fetcher.state !== 'idle', handleFetchBuildHubs];
};
